import type { Column, GetMainMenuItemsParams, GridOptions, IMenuActionParams, MenuItemDef } from 'ag-grid-community';
import { IconName } from '../Icons/IconNames';
import { getCustomColDefProperties } from './columns/baseColumn';
import { agGridMenuSeparator, type AgGridMenuItems } from './types';
import { isGridApiReady, removeUnnecessarySeparators } from './utils';

export function filterBlotterMainMenuDefaultItems(defaultItems: AgGridMenuItems): AgGridMenuItems {
  return defaultItems.filter(item => item !== 'rowUnGroup');
}

export function buildBlotterMainMenuItems({
  params,
  getExtraMainMenuItems,
  columnDynamicsMenuItems,
  onExpandAllRows,
  onCollapseAllRows,
}: {
  params: GetMainMenuItemsParams;
  getExtraMainMenuItems?: GridOptions['getMainMenuItems'];
  columnDynamicsMenuItems?: MenuItemDef[];
  onExpandAllRows?: () => void;
  onCollapseAllRows?: () => void;
}): AgGridMenuItems {
  const extras = getExtraMainMenuItems ? getExtraMainMenuItems(params) : [];
  const extrasWithSeparator = extras.length > 0 ? [...extras, agGridMenuSeparator] : extras;

  const defaultItems = filterBlotterMainMenuDefaultItems(params.defaultItems);

  const resetColumnsIndex = defaultItems.findIndex(item =>
    typeof item === 'string' ? item === 'resetColumns' : item.name === 'Reset Columns'
  );

  if (resetColumnsIndex !== -1) {
    const allColumns = params.api.getColumns() ?? [];
    const hasGroupableColumns = allColumns.some(col => col.getColDef().enableRowGroup === true);

    if (hasGroupableColumns) {
      const resetGroupingItem: MenuItemDef = {
        name: 'Reset Row Grouping',
        action: (actionParams: IMenuActionParams) => {
          if (!isGridApiReady(actionParams.api)) {
            return;
          }

          const initialRowGroupColumns = allColumns
            .filter(col => col.getColDef().initialRowGroup === true)
            .toSorted((a, b) => (a.getColDef().initialRowGroupIndex ?? 0) - (b.getColDef().initialRowGroupIndex ?? 0))
            .map(col => col.getColId());
          actionParams.api.setRowGroupColumns(initialRowGroupColumns);
        },
      };
      defaultItems.splice(resetColumnsIndex + 1, 0, resetGroupingItem);
    }
  }

  replaceItemInMenu(defaultItems, 'expandAll', {
    name: 'Expand All Row Groups',
    icon: `<i class="ag-icon ${IconName.ListExpand}"/>`,
    action: () => {
      onExpandAllRows?.();
      params.api.expandAll();
    },
  });

  replaceItemInMenu(defaultItems, 'contractAll', {
    name: 'Collapse All Row Groups',
    icon: `<i class="ag-icon ${IconName.ListCollapse}"/>`,
    action: () => {
      onCollapseAllRows?.();
      params.api.collapseAll();
    },
  });

  const compositeGroupLeafColumns = getCompositeGroupLeafColumns(params);
  if (compositeGroupLeafColumns) {
    replaceCompositeGroupPinSubMenu(defaultItems, params, compositeGroupLeafColumns);
  }

  if (columnDynamicsMenuItems?.length) {
    defaultItems.push(agGridMenuSeparator, ...columnDynamicsMenuItems);
  }

  return removeUnnecessarySeparators(extrasWithSeparator.concat(defaultItems));
}

function getCompositeGroupLeafColumns(params: GetMainMenuItemsParams): Column[] | undefined {
  const clickedColumn = params.column;
  if (!clickedColumn) {
    return undefined;
  }

  const dynamics = getCustomColDefProperties(clickedColumn.getColDef())?.dynamics;
  if (!dynamics || dynamics.colID === clickedColumn.getColId()) {
    return undefined;
  }

  const parentGroup = clickedColumn.getParent?.();
  const groupLeafColumns = parentGroup?.getDisplayedLeafColumns?.() ?? [];
  if (groupLeafColumns.length > 0) {
    return groupLeafColumns;
  }

  const dynamicLeafColumns = (params.api.getColumns() ?? []).filter(column => {
    const columnDynamics = getCustomColDefProperties(column.getColDef())?.dynamics;
    return columnDynamics?.colID === dynamics.colID;
  });

  return dynamicLeafColumns.length > 0 ? dynamicLeafColumns : undefined;
}

function replaceCompositeGroupPinSubMenu(
  menuItems: AgGridMenuItems,
  params: GetMainMenuItemsParams,
  groupLeafColumns: Column[]
): void {
  const pinIndex = menuItems.findIndex(item => item === 'pinSubMenu');
  if (pinIndex === -1) {
    return;
  }

  const groupColIds = groupLeafColumns.map(column => column.getColId());
  menuItems[pinIndex] = {
    name: 'Pin Column',
    subMenu: [
      {
        name: 'Pin Left',
        action: () => {
          params.api.setColumnsPinned(groupColIds, 'left');
        },
      },
      {
        name: 'Pin Right',
        action: () => {
          params.api.setColumnsPinned(groupColIds, 'right');
        },
      },
      {
        name: 'No Pin',
        action: () => {
          params.api.setColumnsPinned(groupColIds, null);
        },
      },
    ],
  };
}

function replaceItemInMenu(menuItems: AgGridMenuItems, item: string, replacement: MenuItemDef): void {
  const index = menuItems.findIndex(menuItem => menuItem === item);
  if (index !== -1) {
    menuItems[index] = replacement;
  }
}
